import { FC, Suspense, lazy, useCallback, useContext, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import Dropzone from '@/components/common/Dropzone';
import useTranslation from '@/hooks/useTranslation';
import { setLocalStorageItem, setPDF } from '@/utils/fileSystemHelpers';
import { validateInputFile } from '@/utils/organize';
import { AppContext } from './context/AppContext';

const Alert = lazy(() => import('@/components/common/Alert'));
const AdblockAlert = lazy(() => import('@/components/common/AdblockAlert'));
const AdvertisingSection = lazy(() => import('@/components/organize/OrganizePDFFooter'));

type ErrorCategoryType = 'info' | 'error';

interface OrganizePDFProps {
  onFinish?: () => void;
}

const OrganizePDF: FC<OrganizePDFProps> = ({ onFinish }) => {
  const [error, setError] = useState<string | null>(null);
  const [errorCategory, setErrorCategory] = useState<ErrorCategoryType>('info');
  const { t } = useTranslation();
  const { webAppUrl } = useContext(AppContext);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return;
      }

      try {
        const inputFile = acceptedFiles[0];
        validateInputFile(inputFile);
        if (inputFile) {
          const arrayBuffer = await inputFile.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const value = {
            contents: uint8Array,
            mode: 33206,
            timestamp: new Date(),
          };

          const key = `/data/${uuidv4()}`;
          await setPDF(key, value);
          setLocalStorageItem('pdf_file', key);

          window.location.href = webAppUrl ?? 'https://mobisystems.com/mobipdf/online';
        }
      } catch (error: any) {
        setError(error.message);
      }
    },
    [webAppUrl]
  );

  const onReject = useCallback(() => {
    setError(t('pdfextra_organize_tool.errors.only_single_file'));
  }, [t]);

  const resetErrors = useCallback(() => {
    setError(null);
    setErrorCategory('info');
  }, []);

  const currentComponent = useMemo(() => {
    return (
      <Dropzone
        className="!tw-py-[40px]"
        onDrop={onDrop}
        onReject={onReject}
        multiple={false}
        paragraphTranslation="pdfextra_organize_tool.dropzone.start_paragraph"
      />
    );

    // if (process === 'uploading') {
    // TODO: Handle upload procces (use a spinner)
    // }
  }, [onDrop, onReject]);

  return (
    <Suspense>
      <div className="tw-widget-root">
        <div className="tw-relative tw-bg-white tw-py-5 tw-p-5 sm:tw-p-[32px] tw-border-3 tw-border-ui-light-grey tw-rounded-t-[48px]">
          {currentComponent}
          <Alert message={error as string} show={error !== null} category={errorCategory} onClose={resetErrors} />
          <AdblockAlert />
        </div>
        <AdvertisingSection />
      </div>
    </Suspense>
  );
};

export default OrganizePDF;
